import { ChainId, LZ_CHAIN_IDS } from '@src/configs/chainInfo'
import { milesApiV2 } from '@src/libs/milesApiV2'
import { IExchangeData } from '@src/types/exchange'
import { isEVMChain } from '@src/utils/chainUtils'
import { getChecksumAddress } from '@src/utils/evm/address'
import { createContextLogger } from '@src/utils/logger'

interface MilesEstimateParams {
    sender_address: string
    target_address: string
    source_lz_id: number
    dest_lz_id: number
    source_asset: string
    dest_asset: string
    source_quantity: number | string
    dest_quantity: number | string
    is_production: boolean
}

interface MilesEstimateResponse {
    volume: number
    order_multiplier: number
    referral_multiplier: number
    points: number
}

/**
 * Gets the correct LayerZero ID for a chain
 * @param chainId The chain ID
 * @returns The LayerZero ID (v1 or v2) for the chain
 */
export const getLzId = (chainId: ChainId): number => {
    return LZ_CHAIN_IDS[chainId]
}

const logger = createContextLogger('miles.api')

/**
 * Estimates miles to be earned for a swap using the miles API
 * @param sellData The sell token data
 * @param buyData The buy token data
 * @param walletAddress The user's wallet address
 * @returns Promise resolving to the estimated miles and multiplier
 */
export const estimateMiles = async (
    sellData: IExchangeData,
    buyData: IExchangeData,
    walletAddress: string
): Promise<{ miles: number; multiplier: number }> => {
    try {
        // Get LayerZero IDs for source and destination chains
        const sourceLzId = getLzId(sellData.chainId as ChainId)
        const destLzId = getLzId(buyData.chainId as ChainId)

        // Apply checksum to wallet address if it's an EVM address
        const senderAddress = isEVMChain(sellData.chainId) ? getChecksumAddress(walletAddress) : walletAddress
        const targetAddress = isEVMChain(buyData.chainId) ? getChecksumAddress(walletAddress) : walletAddress

        // Prepare request parameters
        const params: MilesEstimateParams = {
            sender_address: senderAddress,
            target_address: targetAddress, // Default to same address
            source_lz_id: sourceLzId,
            dest_lz_id: destLzId,
            source_asset: sellData.token.address,
            dest_asset: buyData.token.address,
            source_quantity: sellData.value.toString(),
            dest_quantity: buyData.value.toString(),
            is_production: true,
        }

        logger.info('miles estimation params:', JSON.stringify(params, null, 2))

        // Call the API to get miles estimate
        const response = await milesApiV2.post<MilesEstimateResponse>('/v1/miles-estimate', params)

        logger.info('miles estimation response:', {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
            data: response.data,
            config: {
                url: response.config?.url,
                method: response.config?.method,
                data: response.config?.data,
            },
        })

        // Calculate total multiplier by multiplying order and referral multipliers
        const totalMultiplier = response.data.order_multiplier * response.data.referral_multiplier

        return {
            miles: response.data.points || 0,
            multiplier: totalMultiplier,
        }
    } catch (error: any) {
        logger.error('Failed to estimate miles:', error)
        return { miles: 0, multiplier: 1 }
    }
}
